import { useMemo } from 'react';
import { versionedAssetUrl } from '@sorrem/engine';
import { useAssetManifest } from '@sorrem-client/ui/hooks/use-asset-manifest';
import type { CharacterHomeTab } from '../tabs/character-home-tabs';

/** A rectangle as fractions (0-1) of the room painting's width and height. */
export interface CharacterHomeSceneRect {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
}

/** One prop laid over the room: its own transparent image, cut from the full painting so it lines up exactly. */
export interface CharacterHomeSceneProp {
    readonly id: string;
    readonly imageUrl: string;
    readonly rect: CharacterHomeSceneRect;
    /** The tab it stands for; it highlights while that tab is hovered. `null` for a prop no tab stands for. */
    readonly tab: CharacterHomeTab | null;
}

/**
 * Character Home's painted room, back to front: the room with its window
 * glass cut out (the Skills tab's Veil sky shows through it), then the
 * props. The character's transparent 3D canvas stands in front of them all.
 */
export interface CharacterHomeScene {
    readonly roomImageUrl: string;
    /** The room painting's width over its height. */
    readonly aspect: number;
    readonly props: readonly CharacterHomeSceneProp[];
    /** The window's glass; it glows while the Skills tab is hovered, and the view zooms through it into the Veil sky when Skills opens. */
    readonly windowRect: CharacterHomeSceneRect;
    /** The writing table; the view zooms onto it when the Journey opens, and settles on its top seen from above. */
    readonly tableRect: CharacterHomeSceneRect;
    /** The writing table's top painted from above, the Journey's books lying open on it. */
    readonly tableTopImageUrl: string;
}

const ASSET_ROOT = '/assets/character-home';

/**
 * Prop rects are printed by `tools/cutscene-art/scripts/cut-character-home.mjs`,
 * which cuts the props from the approved painting; re-run it and copy them
 * here whenever the painting changes.
 */
const WRITING_TABLE_RECT: CharacterHomeSceneRect = { left: 0, top: 0.4154, width: 0.2442, height: 0.5417 };

const CHARACTER_HOME_SCENE: CharacterHomeScene = {
    roomImageUrl: `${ASSET_ROOT}/room.webp`,
    aspect: 1376 / 768,
    props: ([
        { id: 'vessel', tab: null, rect: { left: 0.1991, top: 0.2005, width: 0.125, height: 0.2917 } },
        { id: 'writing-table', tab: 'journey', rect: WRITING_TABLE_RECT },
        { id: 'map', tab: 'crucibles', rect: { left: 0.8052, top: 0.1927, width: 0.1177, height: 0.2982 } },
        { id: 'workbench', tab: 'crafting', rect: { left: 0.6272, top: 0.2969, width: 0.2827, height: 0.5911 } },
        { id: 'chest', tab: 'inventory', rect: { left: 0.8038, top: 0.707, width: 0.1061, height: 0.2526 } },
        { id: 'gear-stand', tab: 'inventory', rect: { left: 0.891, top: 0.2318, width: 0.109, height: 0.7682 } },
    ] satisfies readonly Omit<CharacterHomeSceneProp, 'imageUrl'>[]).map((prop) => ({ ...prop, imageUrl: `${ASSET_ROOT}/props/${prop.id}.webp` })),
    windowRect: { left: 0.4506, top: 0.1875, width: 0.1047, height: 0.25 },
    tableRect: WRITING_TABLE_RECT,
    tableTopImageUrl: `${ASSET_ROOT}/writing-table-top.webp`,
};

/** The scene with every image at its versioned URL (#285), so the precached paintings need no request. */
function versionScene(scene: CharacterHomeScene): CharacterHomeScene {
    return {
        ...scene,
        roomImageUrl: versionedAssetUrl(scene.roomImageUrl),
        props: scene.props.map((prop) => ({ ...prop, imageUrl: versionedAssetUrl(prop.imageUrl) })),
        tableTopImageUrl: versionedAssetUrl(scene.tableTopImageUrl),
    };
}

/** The painted room Character Home's character stands in. */
export function useCharacterHomeScene(): CharacterHomeScene {
    const manifest = useAssetManifest();
    return useMemo(() => (manifest === null ? CHARACTER_HOME_SCENE : versionScene(CHARACTER_HOME_SCENE)), [manifest]);
}
